import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { getFilterStore } from './useFilterStore';
import { SEARCH_TYPE } from '@/constants/searchType';
import {
  hasPublicSearchUrlState,
  parsePathnameToSearchType,
} from '@/lib/publicSearchPaths';

type SearchStore = {
  basicSearchInput: string;
  /** Last natural-language query used for a successful AI search (animal tab). */
  aiSearchPrompt: string;
  openResult: boolean;
  openBasicSearchDialog: boolean;
  isAiMode: boolean;
  setBasicSearch: (term: string) => void;
  /** Updates the visible search string without recomputing flat filters (e.g. restoring AI prompt). */
  setSearchDisplayTerm: (term: string) => void;
  setAiSearchPrompt: (prompt: string) => void;
  setOpenResult: (value: boolean) => void;
  setOpenBasicSearchDialog: (value: boolean) => void;
  setIsAiMode: (value: boolean) => void;
};

const useSearchStore = create<SearchStore>()(
  persist(
    devtools(
      immer((set) => ({
        basicSearchInput: '',
        aiSearchPrompt: '',
        openResult: false,
        openBasicSearchDialog: false,
        isAiMode: false,

        setBasicSearch: (searchTerm) =>
          set((_state) => {
            const pathname =
              typeof window !== 'undefined' ? window.location.pathname : null;
            const searchType =
              parsePathnameToSearchType(pathname) ?? SEARCH_TYPE.ANIMAL;

            getFilterStore(searchType)
              .getState()
              .computeFiltersFromSearchTerm(searchTerm);

            return {
              basicSearchInput: searchTerm,
            };
          }),

        setSearchDisplayTerm: (term) =>
          set(() => ({
            basicSearchInput: term,
          })),

        setAiSearchPrompt: (prompt) =>
          set((_state) => ({
            aiSearchPrompt: prompt,
          })),

        setOpenResult: (value) =>
          set((_state) => ({
            openResult: value,
          })),

        setOpenBasicSearchDialog: (value) =>
          set((_state) => ({
            openBasicSearchDialog: value,
          })),

        setIsAiMode: (value) =>
          set(() => ({
            isAiMode: value,
          })),
      })),
      { name: 'SearchStore' },
    ),
    {
      name: 'SearchStore',
      storage: createJSONStorage(() => sessionStorage),
      // Defer to the URL: if the page wasn't opened with `?search=` state,
      // discard the previous session's query so a fresh visit to e.g.
      // /{org}/animals starts empty instead of re-running the stale query
      // (and racing usePublicSearchUrlSync into a double load / flicker).
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<SearchStore>) };
        if (hasPublicSearchUrlState()) return merged;
        // `openResult` is intentionally left untouched: the list pages always
        // show the results grid (an empty search renders the default list),
        // so forcing it false here would hide the grid for a frame on a fresh
        // visit. Only the stale query content is cleared.
        return {
          ...merged,
          basicSearchInput: '',
          aiSearchPrompt: '',
          isAiMode: false,
        };
      },
    },
  ),
);

export default useSearchStore;
